import Highcharts, { Options } from 'highcharts';
import cssVariables from '@/assets/cssVariables.json';
import { Messages } from '@/constants/Messages.ts';
import { getThemeVariablesForPalette } from '@/constants/ColourPalettes.ts';
import type { ColourPaletteId, ThemeVariables } from '@/constants/ColourPalettes.ts';

const defaultFontStyle = 'Montserrat,sans-serif';
const defaultHeaderSize = '16px';
const defaultFontSize = '10px';
// const defaultLegendItemSize = '10px';
// const defaultAxisLineColor = 'rgb(141, 168, 173)';

export const baseBoxChartOptions: Highcharts.Options = {
    chart: {
        type: 'boxplot',
    },
    title: {
        text: '',
    },
    xAxis: {
        title: {
            text: '',
        },
        categories: [],
        type: 'category',
    },
    yAxis: {
        title: {
            text: '',
        },
    },
    legend: {
        enabled: true,
        layout: 'horizontal',
        align: 'center',
        verticalAlign: 'bottom',
        title: {
            text: '',
        },
    },
    tooltip: {
    },
    plotOptions: {
        boxplot: {
            fillColor: '#EFEFEF',
            pointWidth: 20, // Width of the box
            lineWidth: 4,
            medianColor: '#F0F0E0',
            medianWidth: 3,
            stemColor: '#F0F0E0',
            stemDashStyle: 'Solid',
            stemWidth: 1,
            whiskerColor: '#F0F0E0',
            whiskerLength: '150%',
            whiskerWidth: 3,
        },
    },
    series: [],
};

const chartGlobalSettings: Options = {
    lang: {
        noData: Messages.NoData,
    },
    chart: {
        animation: false,
        // height: 400,
        spacingBottom: 0,
        // spacingTop: 5,
        spacingRight: -5,
        spacingLeft: -5,
        borderColor: 'none',
        style: {
            fontFamily: defaultFontStyle,
        },
        zooming: {
            mouseWheel: {
                enabled: false,
            },
        },
        panning: {
            enabled: true,
        },
        panKey: 'shift',
    },
    noData: {
        style: {
            fontWeight: 'bold',
            fontSize: '15px',
        },
    },
    credits: {
        enabled: false,
        href: '',
        style: {
            fontSize: defaultFontSize,
        },
    },
    accessibility: {
        enabled: false,
    },
    exporting: {
        enabled: false,
        buttons: {
            contextButton: {
                theme: {
                    fill: '#00a3c7',
                },
                menuItems: [
                    'printChart',
                    'separator',
                    'downloadPNG',
                    'downloadJPEG',
                    'downloadPDF',
                    'downloadSVG',
                ],
            },
        },
        chartOptions: {
            chart: {
                backgroundColor: cssVariables.theme.dark.chart.chartBackground,
            },
            legend: {
                maxHeight: 100,
                margin: 0,
                width: '100%',
            },
        },
    },
    time: {
        timezone: 'Europe/London',
    },
    legend: {
        borderRadius: 6,
        borderWidth: 0,
        bubbleLegend: {
            enabled: false,
            borderWidth: 1,
            connectorDistance: 40,
            ranges: [{ color: 'rgba(233, 63, 111, 0.7)' }, { color: 'rgba(247, 166, 0, 0.7)' }, { color: 'rgba(0, 163, 199, 0.7)' }],
        },
        enabled: true,
        itemStyle: {
            fontSize: cssVariables.fonts.extraSmallFontSize,
            fontWeight: 'normal',
        },
        // width: '100%',
        itemDistance: 10,
        // x: 10,
        maxHeight: 60,
        margin: 0,
    },
    plotOptions: {
        bar: {
            borderWidth: 0,
        },
        ohlc: {
            lineWidth: 2.5,
        },
        scatter: {
            marker: {
                radius: 5,
            },
        },
        line: {
            marker: {
                enabled: false,
            },
        },
        series: {
            dataGrouping: {
                enabled: false,
            },
            showInNavigator: true,
            animation: false,
            boostThreshold: 0,
        },
        column: {
            borderWidth: 0,
            stacking: 'normal',
            dataLabels: {
                enabled: false,
            },
            groupPadding: 0,
            pointPadding: 0.1,
        },
        area: {
            stacking: 'normal',
            dataLabels: {
                enabled: false,
            },
            marker: {
                enabled: false,
            },
            trackByArea: true,
        },
        boxplot: {
            lineWidth: 3,
            medianWidth: null,
            fillColor: 'white',
        },
    },
    navigator: {
        enabled: false,
    },
    scrollbar: {
        enabled: false,
    },
    rangeSelector: {
        enabled: false,
    },
    series: [],
    title: {
        style: {
            fontSize: defaultHeaderSize,
            fontWeight: 'normal',
        },
        text: '',
    },
    tooltip: {
        style: {
            zIndex: 9999,
        },
        animation: false,
        borderRadius: 15,
        borderWidth: 0,
        padding: 0,
        followPointer: true,
        hideDelay: 0,
        backgroundColor: undefined,
        split: true,
        shared: false,
        outside: true,
        useHTML: true,
    },
    navigation: {
        buttonOptions: {
            verticalAlign: 'top',
            y: -20,
        },
    },
    xAxis: {
        crosshair: true,
        ordinal: false,
        labels: {
            style: {
                fontSize: defaultFontSize,
            },
        },
        lineWidth: 2,
        title: {
            align: 'middle',
            style: {
                fontSize: defaultFontSize,
            },
            text: '',
        },
        endOnTick: false,
    },
    yAxis: {
        labels: {
            style: {
                fontSize: defaultFontSize,
            },
        },
        title: {
            style: {
                fontSize: defaultFontSize,
            },
            margin: 2,
            text: '',
        },
        stackLabels: {
            enabled: false,
            style: {
                fontSize: defaultFontSize,
            },
        },
        opposite: false,
        endOnTick: false,
    },
};

function getThemeFromVariables (themeVariables: ThemeVariables): Options {
    const chartVariables = themeVariables.chart;
    const chartLineColour = chartVariables.chartLineColour;
    const gridLineColour = chartVariables.chartGridLineColour;
    const themeChartTextColour = chartVariables.chartTextColour;
    const themeChartTextHoverColour = chartVariables.chartTextHoverColour;
    return {
        lang: {
            noData: Messages.NoData,
        },
        chart: {
            backgroundColor: chartVariables.chartBackground,
        },
        noData: {
            style: {
                color: themeChartTextColour,
            },
        },
        credits: {
            style: {
                color: themeChartTextColour,
            },
        },
        legend: {
            itemStyle: {
                color: themeChartTextColour,
            },
            itemHoverStyle: {
                color: themeChartTextHoverColour,
            },
            navigation: {
                style: {
                    color: themeChartTextColour,
                },
                activeColor: themeChartTextColour,
                inactiveColor: themeChartTextColour,
            },
        },
        title: {
            style: {
                color: themeChartTextColour,
            },
        },
        xAxis: {
            labels: {
                style: {
                    color: themeChartTextColour,
                },
            },
            title: {
                style: {
                    color: themeChartTextColour,
                },
            },
            gridLineColor: gridLineColour,
            tickColor: chartLineColour,
            lineColor: chartLineColour,
        },
        yAxis: {
            labels: {
                style: {
                    color: themeChartTextColour,
                },
            },
            title: {
                style: {
                    color: themeChartTextColour,
                },
            },
            stackLabels: {
                style: {
                    color: themeChartTextColour,
                },
            },
            gridLineColor: gridLineColour,
            tickColor: chartLineColour,
            lineColor: chartLineColour,
        },
        tooltip: {
            hideDelay: 0,
        },
    };
}

function getTheme (themeOrPaletteId: boolean | ColourPaletteId): Options {
    const themeVariables = typeof themeOrPaletteId === 'boolean'
        ? getThemeVariablesForPalette(null, themeOrPaletteId)
        : getThemeVariablesForPalette(themeOrPaletteId);

    return getThemeFromVariables(themeVariables);
}

const transparentBackgroundTheme: Options = {
    chart: {
        backgroundColor: 'transparent',
    },
    xAxis: {
        lineColor: 'var(--chartLineColour)',
        gridLineColor: 'var(--chartGridLineColour)',
    },
    yAxis: [
        {
            lineColor: 'var(--chartLineColour)',
            gridLineColor: 'var(--chartGridLineColour)',
        },
        {
            lineColor: 'var(--chartLineColour)',
            gridLineColor: 'var(--chartGridLineColour)',
        },
    ],
    legend: {
        itemStyle: {
            color: 'var(--primaryFont)',
        },
    },
};

function updateChartsThemeStyle (themeVariables: ThemeVariables): void {
    Highcharts.theme = getThemeFromVariables(themeVariables);
    Highcharts.setOptions(Highcharts.theme);
}

export {
    chartGlobalSettings, updateChartsThemeStyle, defaultFontSize, transparentBackgroundTheme, getTheme, getThemeFromVariables,
};
